import {offlineDb} from 'src/offline/db';
import {deleteSessionFile} from 'src/offline/files';
import {deleteItem} from 'src/offline/outbox';
import type {OutboxItem} from 'src/offline/types';
import {INSPECTION_OUTBOX_KINDS} from './executors';
import {rebuildLocalInspectionState} from './localState';
import {parseInspectionContextKey} from './snapshotKeys';

const isAttachment = (item: {kind: string}): boolean =>
  item.kind === INSPECTION_OUTBOX_KINDS.addAttachment;

// Deletes the captured blob a discarded photo item stored (refKey = its
// clientSubmissionId), so it isn't orphaned in the files table.
const deleteCapturedFile = (item: OutboxItem): Promise<void> =>
  deleteSessionFile(item.sessionId, item.clientSubmissionId);

// Discards a queued item (plus a custom-device create's still-queued dependents)
// then rebuilds local state so applier-written snapshots reflect the removal.
export const discardItemAndDependents = async (
  item: OutboxItem
): Promise<void> => {
  if (item.kind === INSPECTION_OUTBOX_KINDS.addCustomDevice) {
    const tempDeviceId = (item.payload as {tempDeviceId?: number}).tempDeviceId;
    const siblings = await offlineDb.outbox
      .where('sessionId')
      .equals(item.sessionId)
      .toArray();
    for (const sibling of siblings) {
      if (sibling.id === item.id) continue;
      const dependentDeviceId = (sibling.payload as {deviceId?: number})
        .deviceId;
      if (
        dependentDeviceId === tempDeviceId &&
        (sibling.status === 'pending' || sibling.status === 'failed')
      ) {
        if (isAttachment(sibling)) await deleteCapturedFile(sibling);
        await deleteItem(sibling.id);
      }
    }
  }

  if (isAttachment(item)) await deleteCapturedFile(item);
  await deleteItem(item.id);

  const session = await offlineDb.sessions.get(item.sessionId);
  const inspectionId = parseInspectionContextKey(session?.contextKey);
  if (inspectionId !== undefined) {
    await rebuildLocalInspectionState(item.sessionId, inspectionId);
  }
};
